import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import {
  Box,
  Button,
  Separator,
} from "@salestrak/ui";

import { PageHeader } from "#app/components/page-header";
import { MembersTable } from "#app/components/members/members-table";
import { useEntityDetail } from "#app/components/entity";
import { InviteMembersDialog } from "#app/components/members/invite-members-dialog";
import { useListQueryState } from "#app/lib/hooks/use-list-query-state";
import { MEMBER_LIST } from "#app/lib/filters/list-configs";
import type { RoleRow, TeamOption } from "#app/lib/data";
import { useCanInvite } from "#app/lib/hooks/use-can-invite";
import { useMemberMutations } from "#app/lib/hooks/use-member-mutations";
import { useSetReportsTo } from "#app/lib/hooks/use-set-reports-to";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { usePermission } from "#app/lib/hooks/use-permission";
import { RequireMemberBrowse } from "#app/components/permissions/require-permission";
import { AnalyticsContext, MemberEvent, useTrackSearch } from "#app/lib/analytics";

function MembersScreen() {
  const canManageUsers = usePermission("manage.users");
  const canInvite = useCanInvite();
  const [filterWhere, setFilterWhere] = useState<Record<string, unknown>>();

  const {
    searchValue,
    setSearchValue,
    sorting,
    setSorting,
    searchWhere,
    orderBy,
    where,
  } = useListQueryState(MEMBER_LIST, { filterWhere });
  useTrackSearch(searchValue, () => ({
    context: AnalyticsContext.Member,
    event: MemberEvent.Search,
    data: { via: "members_table" },
  }));

  // Sibling data extracted from the ungrouped query.
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const handleSiblingData = useCallback((r: RoleRow[], t: TeamOption[]) => {
    setRoles(r);
    setTeams(t);
  }, []);

  // Dialog state
  const { openEntity } = useEntityDetail();
  const [inviteOpen, setInviteOpen] = useState(false);

  const handleViewProfile = useCallback(
    (userId: string) => openEntity("user", userId, { via: "members_list" }),
    [openEntity],
  );

  // Role management inline on this view, same commit shape as the settings
  // roster: the picked role replaces whatever the member held.
  const { updateMemberRoles, updateMemberTeams } = useMemberMutations();
  const handleRoleChange = useCallback(
    async (userId: string, currentRoleIds: string[], roleId: string) => {
      const isSelected = currentRoleIds.includes(roleId);
      const add = isSelected ? [] : [roleId];
      const remove = isSelected ? [roleId] : currentRoleIds.filter((id) => id !== roleId);
      await updateMemberRoles(userId, { add, remove });
    },
    [updateMemberRoles],
  );

  // Reporting line — the same write the profile panel's Reports-to row
  // commits (null clears; a pick from the member's own line lifts them up).
  const setReportsTo = useSetReportsTo("members_table");
  const handleManagerChange = useCallback(
    async (userId: string, managerId: string | null) => {
      await setReportsTo(userId, managerId);
    },
    [setReportsTo],
  );

  // Team membership, inline — per-team scoped like the settings table: the
  // cell's picker offers exactly the teams the viewer can manage (the
  // server-computed `viewerCanManage` on the workspace team map), so every
  // toggle is one the server's per-team gate will accept. No manageable
  // team = read-only display.
  const { teams: workspaceTeams } = useWorkspaceData();
  const manageableTeamIds = useMemo(
    () =>
      new Set(
        workspaceTeams
          .filter((t) => t.viewerCanManage)
          .map((t) => t.teamId),
      ),
    [workspaceTeams],
  );
  const handleTeamsChange = useCallback(
    async (userId: string, currentTeamIds: string[], newTeamIds: string[]) => {
      await updateMemberTeams(userId, currentTeamIds, newTeamIds);
    },
    [updateMemberTeams],
  );

  return (
    <Box direction="column" fill="flex">
      <PageHeader
        title="All Members"
        actions={
          // Invite visibility is grant-driven (role_invite_grant).
          canInvite ? (
            <Button size="xs" onClick={() => setInviteOpen(true)}>
              {/* Raw string: a bare Text element pins 14px into the 24px
                * rung; a string child inherits the button's own 12px font.
                * The word stays on phones too — an icon-only user-plus glyph
                * up here read as an unlabeled mystery, not as Invite. */}
              Invite
            </Button>
          ) : undefined
        }
      />
      <Separator />
      <MembersTable
        variant="app"
        where={where}
        orderBy={orderBy}
        sorting={sorting}
        onSortingChange={setSorting}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        availableTeams={teams}
        availableRoles={roles}
        onViewProfile={handleViewProfile}
        onRoleChange={canManageUsers ? handleRoleChange : undefined}
        onManagerChange={canManageUsers ? handleManagerChange : undefined}
        onTeamsChange={manageableTeamIds.size > 0 ? handleTeamsChange : undefined}
        manageableTeamIds={manageableTeamIds}
        onFilterWhereChange={setFilterWhere}
        searchWhere={searchWhere}
        onSiblingData={handleSiblingData}
      />
      {canInvite && (
        <InviteMembersDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      )}
    </Box>
  );
}

// The directory is a member surface: a share-only viewer is denied (see
// RequireMemberBrowse) — the roster is not shared work.
function MembersRoute() {
  return (
    <RequireMemberBrowse>
      <MembersScreen />
    </RequireMemberBrowse>
  );
}

export const Route = createFileRoute("/(app)/$workspace/members")({
  component: MembersRoute,
});
